import { useMutation, useQueryClient } from '@tanstack/react-query';
import Toast from '../components/Toast';
import { jsonDeleteResponse, jsonPost, jsonPut } from '../api/jsonRequest';
import { queryKeys } from '../query/queryKeys';
import { buildPollUrl } from '../query/queryParams';
import { usePollQuery } from '../query/usePollQuery';

export function useLicenses({ companyId, type, enabled = true, pauseRefetch = false }) {
    const url = buildPollUrl('/api/Account/FetchLicenses', companyId, type);

    return usePollQuery({
        queryKey: queryKeys.licenses.list(companyId, type),
        url,
        enabled,
        pauseRefetch,
    });
}

export function useCreateLicense() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (payload) => {
            const res = await jsonPost('/api/Account/PostLicense', payload);
            if (res.status !== 200) {
                throw new Error('Creation failed');
            }
            return res.data;
        },
        onSuccess: (_data, payload) => {
            Toast.fire({
                title: `${payload.LicenseQuantity} License(s) Generated`,
                icon: 'success',
            });
            void queryClient.invalidateQueries({ queryKey: queryKeys.licenses.all() });
        },
        onError: () => {
            Toast.fire({
                title: 'Creation failed',
                icon: 'error',
            });
        },
    });
}

export function useUpdateLicense() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (payload) => {
            const res = await jsonPut('/api/Account/UpdateLicense', payload);
            if (res.status !== 200) {
                throw new Error('Update failed');
            }
            return res.data;
        },
        onSuccess: () => {
            Toast.fire({
                title: 'License Notes Updated',
                icon: 'success',
            });
            void queryClient.invalidateQueries({ queryKey: queryKeys.licenses.all() });
        },
    });
}

export function useRevokeLicense() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (licenseId) => {
            const res = await jsonDeleteResponse(`/api/Account/DeleteLicense/${licenseId}`);
            if (res.status !== 200) {
                throw new Error('Revoke failed');
            }
            return res.data;
        },
        onSuccess: () => {
            Toast.fire({
                title: 'License Revoked',
                icon: 'warning',
            });
            void queryClient.invalidateQueries({ queryKey: queryKeys.licenses.all() });
        },
    });
}
